import React from 'react';
import { Box, useMenuContext, forwardRef } from '@chakra-ui/react';

export interface ActionMenuCloseProps<T = unknown> {
  children?: React.ReactNode;
  /**
   * The `closeOnSuccess` prop allows you to pass in a method that expects a
   * promise to be returned. When that promise is resolved the menu will close.
   * If the promise is rejected the menu will stay open. This is useful when
   * you want to create a "Save" or "Apply" type of button and the action needs
   * to communicate with an API.
   */
  closeOnSuccess?: () => Promise<T>;
}

/**
 * The `ActionMenuClose` provides a wrapper over a component, typically a
 * button, that will close the menu when it is pressed.
 */
export const ActionMenuClose = forwardRef<ActionMenuCloseProps, 'div'>(
  ({ children, closeOnSuccess, ...rest }, ref) => {
    const { onClose } = useMenuContext();
    const onClickHandler = () => {
      if (closeOnSuccess) {
        // Only execute onClose when the promise is resolved/successful
        closeOnSuccess().then(() => onClose());
      } else {
        onClose();
      }
    };
    return (
      <Box ref={ref} __css={{ flex: '1' }} onClick={onClickHandler} {...rest}>
        {children}
      </Box>
    );
  }
);
